import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import Nav from '../../components/Nav';
import { useAxios } from '../../utils/hook/useAxios';
import { useUser } from '../../utils/hook/UserContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const LABELS = [
  'Jan.',
  'Fév.',
  'Mars',
  'Avr.',
  'Mai',
  'Juin',
  'Juil.',
  'Août',
  'Sept.',
  'Oct.',
  'Nov.',
  'Déc.',
];

const PAIEMENT_STATUS = {
  0: { label: 'En attente', bg: 'bg-warning-subtle text-warning', icon: 'bi-hourglass-split' },
  1: { label: 'Viré', bg: 'bg-success-subtle text-success', icon: 'bi-check-circle-fill' },
  2: { label: 'Annulé', bg: 'bg-danger-subtle  text-danger', icon: 'bi-x-circle-fill' },
  3: { label: 'Litige', bg: 'bg-danger-subtle  text-danger', icon: 'bi-exclamation-triangle-fill' },
  4: { label: 'Remboursé', bg: 'bg-info-subtle    text-info', icon: 'bi-arrow-counterclockwise' },
};

const STATUS_MAP = {
  pending: { label: 'En attente', bg: 'bg-warning-subtle text-warning' },
  accepted: { label: 'Acceptée', bg: 'bg-primary-subtle text-primary' },
  paid: { label: 'Confirmée', bg: 'bg-success-subtle text-success' },
  completed: { label: 'Terminée', bg: 'bg-success-subtle text-success' },
  rejected: { label: 'Annulée', bg: 'bg-danger-subtle text-danger' },
};

function fmtEur(n) {
  const val = parseFloat(n) || 0;
  if (val >= 1000) return (val / 1000).toFixed(1).replace('.0', '') + 'k €';
  return val.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

function KpiCard({ icon, color, label, value, sub }) {
  return (
    <div className="card border-0 shadow-sm rounded-3">
      <div className="card-body px-3 py-2 d-flex align-items-center gap-3">
        <div
          className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
          style={{ width: 36, height: 36, background: `${color}18` }}
        >
          <i className={`bi ${icon}`} style={{ color, fontSize: 16 }}></i>
        </div>
        <div className="flex-grow-1 overflow-hidden">
          <p className="mb-0 text-muted text-truncate" style={{ fontSize: 12 }}>
            {label}
          </p>
          {sub && (
            <p className="mb-0 text-muted" style={{ fontSize: 10 }}>
              {sub}
            </p>
          )}
        </div>
        <p className="mb-0 fw-bold flex-shrink-0" style={{ fontSize: 16, color }}>
          {value}
        </p>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="card border-0 shadow-sm rounded-3 mb-3">
      <div className="card-body p-3 d-flex gap-3 align-items-center">
        <div
          className="rounded-2 bg-secondary bg-opacity-25 flex-shrink-0"
          style={{ width: 72, height: 56 }}
        />
        <div className="flex-grow-1">
          <div
            className="bg-secondary bg-opacity-25 rounded mb-2"
            style={{ height: 12, width: '40%' }}
          />
          <div
            className="bg-secondary bg-opacity-25 rounded"
            style={{ height: 10, width: '60%' }}
          />
        </div>
        <div className="bg-secondary bg-opacity-25 rounded" style={{ height: 20, width: 60 }} />
      </div>
    </div>
  );
}

function AccountEarnings() {
  const axios = useAxios();

  const [chartData, setChartData] = useState(null);
  const [vehiclesList, setVehiclesList] = useState(null);
  const [totals, setTotals] = useState({ attendus: 0, engendres: 0, manques: 0 });
  const [isLoading, setIsLoading] = useState(true);

  // Virements réels depuis la table Paiements
  const [paiements, setPaiements] = useState([]);
  const [paiementsLoaded, setPaiementsLoaded] = useState(false);
  const { user, loading, isAuthenticated } = useUser();

  useEffect(() => {
    // Gains (reservationsGain)
    axios
      .get('/reservationsGain/host/' + user?.id)
      .then(({ data }) => {
        const gainsAttendus = new Array(12).fill(0);
        const gainsEngendres = new Array(12).fill(0);
        const gainsManques = new Array(12).fill(0);
        let totalAttendus = 0,
          totalEngendres = 0,
          totalManques = 0;

        data.forEach((item) => {
          const m = new Date(item.createdAt).getMonth();
          const amount = parseFloat(item.amount);
          if (item.type === '0') {
            gainsAttendus[m] += amount;
            totalAttendus += amount;
          } else if (item.type === '1') {
            gainsManques[m] += amount;
            totalManques += amount;
          } else if (item.type === '2') {
            gainsEngendres[m] += amount;
            totalEngendres += amount;
          }
        });

        setTotals({ attendus: totalAttendus, engendres: totalEngendres, manques: totalManques });
        setChartData({
          labels: LABELS,
          datasets: [
            { label: 'Gains attendus', data: gainsAttendus, backgroundColor: '#0d6efd' },
            { label: 'Gains engendrés', data: gainsEngendres, backgroundColor: '#22c55e' },
            { label: 'Gains manqués', data: gainsManques, backgroundColor: '#ef4444' },
          ],
        });

        const vehiclesMap = {};
        data.forEach((t) => {
          const v = t.Reservation?.Vehicle;
          if (!v) return;
          const id = v.id;
          if (!vehiclesMap[id])
            vehiclesMap[id] = { vehicleInfo: v, totalAmount: 0, reservations: [] };
          vehiclesMap[id].totalAmount += parseFloat(t.amount);
          vehiclesMap[id].reservations.push(t.Reservation);
        });
        setVehiclesList(Object.values(vehiclesMap));
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));

    // Paiements réels (virements)
    axios
      .get('/paiements')
      .then(({ data }) => {
        if (Array.isArray(data)) {
          // Filtrer ceux où l'hôte est l'utilisateur courant
          const mine = data.filter((p) => p.Reservation?.Host?.id === user?.id);
          setPaiements(mine.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        }
      })
      .catch(() => {})
      .finally(() => setPaiementsLoaded(true));
  }, []);

  const year = new Date().getFullYear();

  // Calculs virements
  const virementsAttente = paiements
    .filter((p) => p.paiementStatus === '0')
    .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const virementsVires = paiements
    .filter((p) => p.paiementStatus === '1')
    .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const virementsLitige = paiements.filter((p) => p.paiementStatus === '3').length;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
      title: { display: false },
      tooltip: {
        callbacks: { label: (ctx) => ` ${ctx.dataset.label} : ${ctx.parsed.y.toFixed(2)} €` },
      },
    },
    scales: {
      x: { stacked: true, ticks: { font: { size: 10 } } },
      y: { stacked: true, ticks: { font: { size: 10 }, callback: (v) => v + ' €' } },
    },
  };

  return (
    <section className="bg-body-tertiary min-vh-100">
      <div className="container py-0 pb-4">
        <Nav />

        {/* ── Hero banner ── */}
        <div
          className="rounded-3 p-3 p-md-4 mb-4 text-white"
          style={{ background: 'linear-gradient(135deg,#0d6efd,#0a58ca)' }}
        >
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-white bg-opacity-25 flex-shrink-0"
              style={{ width: 52, height: 52 }}
            >
              <i className="bi bi-cash-stack" style={{ fontSize: 24 }}></i>
            </div>
            <div className="flex-grow-1">
              <p className="mb-0 opacity-75" style={{ fontSize: 12 }}>
                Revenus totaux {year}
              </p>
              <p className="mb-0 fw-bold" style={{ fontSize: 28 }}>
                {fmtEur(totals.engendres)}
              </p>
              <p className="mb-0 opacity-75" style={{ fontSize: 11 }}>
                {fmtEur(virementsVires)} effectivement virés · {fmtEur(virementsAttente)} en attente
              </p>
            </div>
            <Link to="/earnings" className="btn btn-sm btn-light flex-shrink-0">
              <i className="bi bi-table me-1"></i>
              <span className="d-none d-sm-inline">Historique</span>
            </Link>
          </div>
        </div>

        {/* ── KPI gains ── */}
        <div className="d-flex flex-column gap-2 mb-4">
          <KpiCard
            icon="bi-hourglass-split"
            color="#0d6efd"
            label="Gains attendus"
            value={fmtEur(totals.attendus)}
            sub="Réservations en cours"
          />
          <KpiCard
            icon="bi-check-circle-fill"
            color="#22c55e"
            label="Gains engendrés"
            value={fmtEur(totals.engendres)}
            sub="Voyages confirmés"
          />
          <KpiCard
            icon="bi-x-circle-fill"
            color="#ef4444"
            label="Gains manqués"
            value={fmtEur(totals.manques)}
            sub="Annulations"
          />
        </div>

        {/* ── Suivi des virements ── */}
        <div className="card border-0 shadow-sm rounded-3 mb-4">
          <div className="card-header bg-white border-bottom d-flex align-items-center justify-content-between py-2 px-3">
            <p className="mb-0 fw-semibold" style={{ fontSize: 14 }}>
              <i className="bi bi-send me-2 text-success"></i>Suivi des virements
            </p>
            <div className="d-flex gap-2">
              {virementsLitige > 0 && (
                <span className="badge bg-danger rounded-pill" style={{ fontSize: 10 }}>
                  {virementsLitige} litige(s)
                </span>
              )}
              <span
                className="badge bg-warning-subtle text-warning rounded-pill"
                style={{ fontSize: 10 }}
              >
                {fmtEur(virementsAttente)} en attente
              </span>
            </div>
          </div>
          <div className="card-body p-0">
            {!paiementsLoaded ? (
              <div className="text-center text-muted py-4">
                <div className="spinner-border spinner-border-sm" />
              </div>
            ) : paiements.length === 0 ? (
              <div className="text-center text-muted py-4">
                <i className="bi bi-inbox" style={{ fontSize: 28 }}></i>
                <p className="mt-2 mb-0 small">Aucun virement pour le moment.</p>
              </div>
            ) : (
              <>
                {paiements.slice(0, 8).map((p) => {
                  const ps = PAIEMENT_STATUS[p.paiementStatus] ?? PAIEMENT_STATUS['0'];
                  return (
                    <div
                      key={p.id}
                      className="d-flex align-items-center gap-3 px-3 py-2 border-bottom"
                    >
                      <div
                        className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
                        style={{ width: 32, height: 32, background: '#f8f9fa' }}
                      >
                        <i
                          className={`bi ${ps.icon} ${ps.bg.split(' ')[1]}`}
                          style={{ fontSize: 14 }}
                        ></i>
                      </div>
                      <div className="flex-grow-1 overflow-hidden">
                        <p className="mb-0 fw-semibold text-truncate" style={{ fontSize: 13 }}>
                          Réservation #{p.reservationId}
                        </p>
                        <p className="mb-0 text-muted" style={{ fontSize: 11 }}>
                          {p.Reservation?.Invite?.firstName} {p.Reservation?.Invite?.lastName}
                          {p.transactionID && (
                            <span className="font-monospace ms-2" style={{ fontSize: 10 }}>
                              {p.transactionID.slice(0, 20)}…
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="d-flex flex-column align-items-end flex-shrink-0 gap-1">
                        <span className="fw-bold text-success" style={{ fontSize: 14 }}>
                          {fmtEur(parseFloat(p.amount) * 0.8)}
                        </span>
                        <span className={`badge rounded-pill ${ps.bg}`} style={{ fontSize: 10 }}>
                          <i className={`bi ${ps.icon} me-1`}></i>
                          {ps.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {paiements.length > 8 && (
                  <div className="text-center py-2">
                    <Link
                      to="/earnings"
                      className="btn btn-sm btn-outline-primary rounded-pill"
                      style={{ fontSize: 12 }}
                    >
                      Voir tous les virements ({paiements.length})
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Graphique ── */}
        <div className="card border-0 shadow-sm rounded-3 mb-4">
          <div className="card-header bg-white border-bottom d-flex align-items-center justify-content-between py-2 px-3">
            <p className="mb-0 fw-semibold" style={{ fontSize: 14 }}>
              <i className="bi bi-bar-chart-fill me-2 text-primary"></i>Évolution mensuelle
            </p>
            <span className="text-muted" style={{ fontSize: 12 }}>
              {year}
            </span>
          </div>
          <div className="card-body p-2 p-md-3">
            {chartData ? (
              <Bar options={chartOptions} data={chartData} />
            ) : (
              <div className="d-flex align-items-center justify-content-center py-5 text-muted">
                <div className="spinner-border spinner-border-sm me-2" />
                Chargement...
              </div>
            )}
          </div>
        </div>

        {/* ── Détail par véhicule ── */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <p className="mb-0 fw-semibold" style={{ fontSize: 15 }}>
            <i className="bi bi-car-front me-2 text-primary"></i>Détail par véhicule
          </p>
          <Link to="/earnings" className="btn btn-sm btn-outline-primary" style={{ fontSize: 12 }}>
            Toutes les transactions
          </Link>
        </div>

        {isLoading && [1, 2].map((i) => <SkeletonCard key={i} />)}

        {!isLoading && vehiclesList?.length === 0 && (
          <div className="card border-0 shadow-sm rounded-3">
            <div className="card-body text-center py-5 text-muted">
              <i className="bi bi-cash-coin" style={{ fontSize: 36 }}></i>
              <p className="mt-3 mb-0">Aucune transaction pour le moment.</p>
            </div>
          </div>
        )}

        {vehiclesList?.map((vd, i) => {
          const image = vd.vehicleInfo.images?.split(',')[0];
          return (
            <div key={i} className="card border-0 shadow-sm rounded-3 mb-3 overflow-hidden">
              <div className="card-body p-3">
                <div className="d-flex align-items-center gap-3 mb-3">
                  {image && (
                    <img
                      src={image}
                      alt="Véhicule"
                      className="rounded-2 flex-shrink-0"
                      style={{ width: 72, height: 52, objectFit: 'cover' }}
                    />
                  )}
                  <div className="flex-grow-1 overflow-hidden">
                    <p className="mb-0 fw-semibold text-truncate" style={{ fontSize: 14 }}>
                      {vd.vehicleInfo.VehicleModel?.marque} {vd.vehicleInfo.VehicleModel?.modele}
                    </p>
                    <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
                      {vd.reservations.length} réservation(s)
                    </p>
                  </div>
                  <p className="mb-0 fw-bold text-success flex-shrink-0" style={{ fontSize: 16 }}>
                    {fmtEur(vd.totalAmount)}
                  </p>
                </div>
                <div className="d-flex flex-column gap-1">
                  {vd.reservations.map((r, j) => {
                    const s = STATUS_MAP[r.status] ?? {
                      label: r.status,
                      bg: 'bg-secondary-subtle text-secondary',
                    };
                    return (
                      <div
                        key={j}
                        className="d-flex align-items-center gap-2 px-2 py-1 rounded-2 bg-body-tertiary"
                      >
                        <i className="bi bi-receipt text-muted" style={{ fontSize: 11 }}></i>
                        <span className="text-muted font-monospace" style={{ fontSize: 11 }}>
                          #{r.reservationId}
                        </span>
                        <span
                          className={`badge rounded-pill ms-auto ${s.bg}`}
                          style={{ fontSize: 10 }}
                        >
                          {s.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default AccountEarnings;
